import { apiRequest } from "./queryClient";
import { rescaleBreakdown } from "./cost-breakdown";
import { canonicalTemplateUnit, compatibleTemplateUnits, type TemplateCalculatedItem, type TemplatePriceBand } from "./template-quote-calculations";

/**
 * Reuse Auto Quote's single-work-item estimator for genuinely missing rates.
 * Never replace a library price, a builder edit, or a broken library recipe.
 * The AI supplies prices only: template scope and measurements stay authoritative.
 */
export async function estimateUnpricedTemplateItems<T extends TemplateCalculatedItem>(
  items: T[], band: TemplatePriceBand, projectContext: string,
): Promise<T[]> {
  const result = [...items];
  const pending = items.map((item, index) => ({ item, index })).filter(({ item }) =>
    item.quantity > 0 && !item.costLibraryTaskId && !item.quoteCostRecipe && !item.priceConfirmed
    && (item.pricingIssue === "missing_library" || item.pricingIssue === "ai_error"));
  // Two requests at a time rather than a burst for every unmatched template line.
  for (let offset = 0; offset < pending.length; offset += 2) {
    await Promise.all(pending.slice(offset, offset + 2).map(async ({ item, index }) => {
      try {
        const unit = ({ "m²": "sqm", "m³": "m3", m: "linear_m", each: ["item", "allow"].includes(item.unit) ? "item" : "each" } as Record<string, string>)[canonicalTemplateUnit(item.unit) ?? ""] ?? item.unit;
        const dimensions = item.quantityDerivation?.dimensions.map(d => `${d.label}: ${d.value} ${d.unit}`).join("; ");
        const description = [
          `Estimate ONLY the price of this existing template work item: ${item.name}.`,
          item.description,
          `Return a UNIT RATE per ${unit}, not the total for the job. Set unit to "${unit}" and quantity to 1.`,
          `The builder's actual quantity is ${item.quantity} ${item.unit}; this is context only. Do not change it.`,
          dimensions ? `Builder's measurements/specifications: ${dimensions}.` : "",
          `Pricing band: ${band}. Use the stated work difficulty; do not multiply actual material costs again for the band.`,
          "Preserve the stated scope and material specifications. Do not add work or invent missing dimensions.",
          "Labour, material and plant costs must be non-negative allocations of that one unit's rate.",
        ].filter(Boolean).join("\n");
        const response = await apiRequest("POST", "/api/generate-work-item", {
          description, projectContext, useCostLibraryRates: false,
        });
        const estimate = await response.json();
        const unitCost = estimate?.unitCost;
        if (estimate?.error || typeof unitCost !== "number" || !Number.isFinite(unitCost) || unitCost <= 0
          || typeof estimate.unit !== "string" || !compatibleTemplateUnits(item.unit, estimate.unit)) {
          throw new Error("The AI did not return a valid rate in the item's unit.");
        }
        const allocations = [estimate.labourCost, estimate.materialCost, estimate.plantCost];
        if (allocations.some(n => typeof n !== "number" || !Number.isFinite(n) || n < 0)
          || allocations.reduce((a, b) => a + b, 0) <= 0) {
          throw new Error("The AI did not return a valid cost breakdown.");
        }
        const roundedRate = Math.round(unitCost * 100) / 100;
        if (roundedRate <= 0 || !Number.isFinite(roundedRate * item.quantity)) throw new Error("The AI rate is not usable.");
        const split = rescaleBreakdown({
          labour: estimate.labourCost, material: estimate.materialCost, plant: estimate.plantCost,
        }, roundedRate * item.quantity, item.category);
        result[index] = {
          ...item, unitCost: roundedRate, labourCost: split.labour, materialCost: split.material, plantCost: split.plant,
          priceSource: "ai_estimated", pricingIssue: undefined, pricingWarning: undefined,
        };
      } catch {
        // Keep successful library/AI lines. A failed service call is not a
        // legitimate zero price and must remain retryable and unsaveable.
        result[index] = {
          ...item, unitCost: 0, labourCost: 0, materialCost: 0, plantCost: 0,
          priceSource: undefined, pricingIssue: "ai_error",
          pricingWarning: "AI pricing could not be completed for this item. Retry AI pricing or review its rate.",
        };
      }
    }));
  }
  return result;
}

export function templateItemNeedsPrice(item: TemplateCalculatedItem): boolean {
  return item.quantity > 0 && !item.costLibraryTaskId && !item.priceConfirmed
    && !(item.priceSource === "ai_estimated" && Number.isFinite(item.unitCost) && item.unitCost > 0);
}
